import i18n from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Experiment, Variation } from '@justgiving/experiments';
import loadable from '@loadable/component';

import {
  FundraisingPageFragment,
  PageStatus,
  PageType,
} from '../../../types/__generated__';
import config from '../../client/config';
import { DonateToYourselfModal } from '../../components/DonateToYourselfModal/DonateToYourselfModal';
import { FirstToDonateTotals } from '../../components/Totals/FirstToDonateTotals';
import { Experiments } from '../../constants/experiments';
import PAGE_TYPES from '../../constants/pageTypes';
import {
  buildQuery,
  useFlags,
  usePageMeta,
  usePageOwner,
  usePageQuery,
} from '../../hooks';
import { useScript } from '../../hooks/useScript';
import { useShouldSkipFirstToDonateEnrolment } from '../../hooks/useShouldSkipFirstToDonateEnrolment';
import Fragment from './FundraisingPageFragment.gql';

import styles from './FundraisingPage.scss';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { ActivityTotals } from '../../components/ActivityTotals/ActivityTotals';
import { Banner } from '../../components/Banner/Banner';
import DonationPrompts from '../../components/DonationPrompts/DonationPrompts';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import FundraisingHelmet from '../../components/Helmet/FundraisingHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import PageOwnerAvatar from '../../components/PageOwnerAvatar/PageOwnerAvatar';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import { PropositionBar } from '../../components/PropositionBar/PropositionBar';
import Reason from '../../components/Reason/Reason';
import FundraisingSocialButtons from '../../components/SocialButtons/FundraisingSocialButtons';
import Story from '../../components/Story/Story';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { Totals } from '../../components/Totals/Totals';

const Prompt = loadable(() => import('../../components/Prompt/Prompt'));
const QRModal = loadable(() => import('../../components/QRModal/QRModal'));
const Editbar = loadable(() => import('../../components/Editbar/Editbar'));
const TimelinePageSection = loadable(() =>
  import('../../components/Timeline/TimelinePageSection/TimelinePageSection'),
);
const FitnessSection = loadable(
  () => import('../../components/FitnessSection/FitnessSection'),
  { resolveComponent: module => module.FitnessSection },
);
const GoToImmersiveGiving = loadable(
  () => import('../../components/GoToImmersiveGiving/GoToImmersiveGiving'),
  {
    resolveComponent: module => module.GoToImmersiveGiving,
  },
);
const TeamAndCompany = loadable(() =>
  import('../../components/TeamAndCompany/TeamAndCompany'),
);
const QualarooLoader = loadable(
  () => import('../../components/QualarooLoader/QualarooLoader'),
  { resolveComponent: module => module.QualarooLoader },
);
const StickySummary = loadable(() =>
  import('../../components/StickySummary/StickySummary'),
);
const EncourageInactivityModal = loadable(() =>
  import('../../components/EncourageInactivityModal/EncourageInactivityModal'),
);

const FundraisingPageQuery = buildQuery(Fragment);

export const FundraisingPage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });
  const [inViewRef, isCtaInView] = useInView();
  const { data } = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName, pageType } = usePageMeta();
  const { enableOtgImmersiveGiving } = useFlags();

  // B2C_029 - First to donate
  // Can be removed when experiment has ended
  const shouldSkipFirstToDonateEnrolment = useShouldSkipFirstToDonateEnrolment();

  const name = data?.owner?.name ?? '';
  const eventGivingGroupId = data?.eventGivingGroupId;
  const totalRelatedCompanies = data?.relationships?.companies.totalCount ?? 0;

  const displayTeamAndCompany =
    totalRelatedCompanies > 0 ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  const adminLink =
    pageType === PageType.OnePage.toLowerCase()
      ? `${config.baseUri}/p/${pageShortName}/admin`
      : `${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { isPageOwner } = usePageOwner();
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );
  const isNewPage = Boolean(urlParams.get('newPage'));

  // const shouldShowDonateToYourself = isPageOwner && isNewPage && isModalOpen;
  const shouldShowDonateToYourself = false; // Hiding while prompt service is operating

  // B2C_040 - Trust Proposition Bar
  // Can be removed when experiment has ended
  const language = i18n?.languages?.length
    ? i18n.languages[0].toLowerCase()
    : null;

  const isNotGbLanguage = language !== 'en-gb';
  // B2C_040 - end

  return (
    <Page>
      {shouldShowDonateToYourself && (
        <DonateToYourselfModal
          heading={t('fundraising.donateToYourself.modal.heading')}
          description={t('fundraising.donateToYourself.modal.description')}
          onClose={() => setIsModalOpen(false)}
          donateButtonLabel={t(
            'fundraising.donateToYourself.modal.donateButton',
          )}
        />
      )}
      <IsQRCode>
        <QRModal ownerName={name} />
      </IsQRCode>
      {/*B2C_040 - Trust Proposition Bar*/}
      {/*Can be removed when experiment has ended*/}
      <Experiment
        slug={Experiments.b2c_040_frp_trust_proposition_bar}
        skipEnrolment={isPageOwner || isNewPage || isNotGbLanguage}
      >
        <Variation name="B">
          <PropositionBar />
        </Variation>
      </Experiment>
      <PageAlert />
      <Banner />
      {eventGivingGroupId && (
        <IsPageOwner>
          <Prompt entityId={eventGivingGroupId.toString()} />
        </IsPageOwner>
      )}
      <Editbar adminLink={adminLink} />
      <FundraisingHelmet />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <PageOwnerAvatar />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              <Experiment
                slug={Experiments.b2c_029_first_to_donate}
                skipEnrolment={shouldSkipFirstToDonateEnrolment}
              >
                <Variation name="A">
                  <Totals />
                </Variation>
                <Variation name="B">
                  <FirstToDonateTotals />
                </Variation>
                <Variation name="C">
                  <FirstToDonateTotals />
                </Variation>
              </Experiment>
            </>
          ),
          callsToAction: (
            <div ref={inViewRef} className={styles.ctaWrapper}>
              <FundraisingCTA />
            </div>
          ),
          titlesSupplementary: (
            <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
              <EventDetails />
              <Reason />
              <TeamDetails
                showCreateTeamCTA={pageType !== PAGE_TYPES.ONE_PAGE}
              />
            </div>
          ),
          prompts: <DonationPrompts />,
          causeMini: <AboutCauseMini />,
          main: (
            <>
              <PageSection
                title={t('story.title')}
                className="jg-pages-fundraising__story"
                hasBorder={true}
              >
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <FundraisingSocialButtons />
              </PageSection>
              <TimelinePageSection />
            </>
          ),
          aside: (
            <>
              {enableOtgImmersiveGiving &&
                data?.relationships?.campaigns.nodes[0]?.slug ===
                  'make-a-refugee-shelter-a-home' && <GoToImmersiveGiving />}
              <IsPageOwner>
                <PageSection>
                  <FitnessSection />
                </PageSection>
              </IsPageOwner>
              <ActivityTotals />
              <SupportersList
                noSupportersMessage={t(
                  'fundraising.noSupportersMessage',
                  `Become {{name}}'s first supporter by making a donation`,

                  { replace: { name } },
                )}
                displayDonateButton
                donateButtonExperiments={[
                  // B2C_041 - Donation Prompts Part 2
                  // Remove this experiment and successType when removing this experiment
                  Experiments.b2c_041_fundraising_page_donation_prompts_2,
                  Experiments.b2c_036_donate_button_colour,
                  Experiments.b2c_038_secure_donate_buttons,
                  // B2C_040 - Proposition Bar
                  // Remove this experiment when complete
                  Experiments.b2c_040_frp_trust_proposition_bar,
                ]}
              />
            </>
          ),
          teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
          contentFooterPrimary: <AboutCampaignAndCharitySection />,
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={['onlineAmount', 'offlineAmount', 'textDonations']}
            />
          ),
        }}
      />
      {!isCtaInView && (
        <StickySummary
          donateButtonExperiments={[
            // B2C_041 - Donation Prompts Part 2
            // Remove this experiment and success type when removing this experiment
            Experiments.b2c_041_fundraising_page_donation_prompts_2,
            Experiments.b2c_036_donate_button_colour,
            Experiments.b2c_038_secure_donate_buttons,
            // B2C_029 - First to donate
            // Can be removed when experiment has ended
            Experiments.b2c_029_first_to_donate,
            // B2C_040 - Proposition Bar
            // Remove this experiment when complete
            Experiments.b2c_040_frp_trust_proposition_bar,
          ]}
        />
      )}
      <EncourageInactivityModal />
      <QualarooLoader />
    </Page>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
